/**
 * Money and rate formatting (FE plan §3.8).
 *
 * Money is integer cents everywhere the BE sends it (`*Cents`). Never convert
 * to float dollars for arithmetic — format at the edge only.
 *
 * Rates: the BE mixes units. Several `*Pct` fields are DECIMAL fractions
 * (`effectiveBufferPct` = 0.045 for 4.5%, copied from BufferFeePercentage),
 * others are real percentages (`conversionRatePct`, `changePct`). Every
 * caller states the unit explicitly — there is no default.
 */

const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
const usd0 = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const int = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });

/** 123456 → "$1,234.56". `null`/`undefined` → "—". */
export function formatCents(cents: number | null | undefined, opts?: { whole?: boolean }): string {
  if (cents === null || cents === undefined || !Number.isFinite(cents)) return '—';
  return (opts?.whole ? usd0 : usd).format(cents / 100);
}

/** 12345 → "12,345". */
export function formatCount(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—';
  return int.format(n);
}

export type RateUnit = 'fraction' | 'percent';

/**
 * Formats a rate. `unit: 'fraction'` for 0.045 → "4.50%"; `unit: 'percent'`
 * for 4.5 → "4.50%". `digits` defaults to 2 (the kit's buffer precision).
 */
export function formatRate(value: number | null | undefined, unit: RateUnit, digits = 2): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  const pct = unit === 'fraction' ? value * 100 : value;
  return `${pct.toFixed(digits)}%`;
}

/** Signed change for a delta line: 8.4 → "+8.4%", -3.1 → "−3.1%". Percent unit. */
export function formatChange(pct: number | null | undefined, digits = 1): string {
  if (pct === null || pct === undefined || !Number.isFinite(pct)) return '—';
  const sign = pct > 0 ? '+' : pct < 0 ? '−' : '';
  return `${sign}${Math.abs(pct).toFixed(digits)}%`;
}
